<template>
  <div class="activity-stage">
    <div class="steps-container" v-if="currentRound.steps" transition="fade">
      <div class="fitb-images-container">
        <div class="fitb-images">
          <img
            v-for="step in roundStepsWithImages"
            :class="`image-sentence-${step.stepIndex}`"
            :key="step.stepIndex"
            :src="step.image"
          />
        </div>
      </div>
      <div class="fitb-container">
        <h2 class="instructions-title" transition="fade">
          {{ instructionsLabel }}
        </h2>

        <div
          v-for="step in currentRound.steps"
          :key="step.stepIndex"
          :class="`sentence-${step.stepIndex}`"
          v-show="step.stepIndex % 2 === 0 && progress.stepIndex >= step.stepIndex"
        >
          <h3 transition="fade">
            {{ currentRound.steps[step.stepIndex].prefix }}
          </h3>

          <div class="prefix-sentence">
            <text-display-list>
              <li transition="fade">
                <blinking-cursor-text :text="step.answer" :text-step="step.stepIndex">
                </blinking-cursor-text>
              </li>
            </text-display-list>
          </div>

          <div class="sentence-expl">
            <text-display-list v-if="currentRound.steps[step.stepIndex + 1]">
              <li transition="fade">
                <blinking-cursor-text
                  :text="currentRound.steps[step.stepIndex + 1].answer"
                  :text-step="step.stepIndex + 1"
                >
                </blinking-cursor-text>
              </li>
            </text-display-list>
          </div>
        </div>
      </div>
    </div>
    <slot name="keyboard"></slot>
  </div>
</template>

<script>
import { TweenMax } from 'gsap';

import AssetLoader from 'mixins/AssetLoader';
import ErrorLogger from 'mixins/ErrorLogger';
import TypingActivityHelper from 'mixins/TypingActivityHelper';
import ActivityManager from 'mixins/ActivityManager';

import BlinkingCursorText from 'components/text-displays/BlinkingCursorText.vue';
import TextDisplayList from 'components/text-displays/TextDisplayList.vue';

export default {
  components: {
    BlinkingCursorText,
    TextDisplayList
  },

  mixins: [AssetLoader, TypingActivityHelper, ActivityManager, ErrorLogger],

  data() {
    return {
      activityName: 'Fill In The Blanks',
      instructionsLabel: ''
    };
  },
  beforeDestroy() {
    TweenMax.killAll();
  },
  computed: {
    roundStepsWithImages() {
      return this.currentRound.steps.filter(step => step.image);
    }
  },
  methods: {
    async loadActivity() {
      const roundSetup = this.getActivityState();
      this.createRounds(roundSetup);
      const {
        instructions: { written }
      } = roundSetup;
      this.instructionsLabel = written;
      await Promise.all([this.loadImages(), this.loadSounds()]);
    },

    async loadImages() {
      const imagePaths = this.rounds[0].steps.map(step => step.image);
      this.queueImage(imagePaths);
      return this.downloadImages();
    },

    async loadSounds() {
      const soundPaths = this.rounds[0].steps.map(step => step.audio);
      this.queueAudio(soundPaths);
      return this.downloadAudio();
    },

    getActivityState() {
      const { rounds } = this.activityData;
      return {
        rounds,
        instructions: {
          written: 'Type the suffix to fill in the blanks. Then type the sentence.',
          voice: `${this.voiceInstructionsPrefix}/fillintheblanks-${this.getRandomVoice()}-1.mp3`,
          delay: 0
        }
      };
    },

    startActivity() {
      this.setupNewRound();
      this.enableKeyboard();
      this.errorLoggerStart();
    },

    handleCorrectKey() {
      this.sndType();
      this.incrementTypingIndex();

      if (this.isEndOfCurrentSentence) {
        this.finishStep();
      }

      if (this.isEndOfRound) {
        this.handleRoundEnd();
      }
    },

    finishStep() {
      const { audio } = this.currentRound.steps[this.progress.stepIndex];
      if (audio) {
        this.sndBellAndAfterPlayOtherHowl(this.cache.get(audio));
      } else {
        this.sndBell();
      }
      this.completeStep();
      this.setCurrentStep();
      if (this.currentRound.steps[this.progress.stepIndex]) {
        const { image, stepIndex } = this.currentRound.steps[this.progress.stepIndex];
        if (image) {
          TweenMax.to(`.image-sentence-${stepIndex}`, 1, {
            css: { autoAlpha: 1 }
          });
        }
      }
    },

    handleRoundEnd() {
      if (this.isEndOfActivity) {
        TweenMax.delayedCall(1, this.completeActivity);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import 'src/assets/sass/fade.scss';

.activity-stage {
  height: 100%;
}
ul {
  list-style: none;
  margin-top: auto;
  padding-inline-start: 0px;
}
</style>
